import { useEffect, useState, type JSX } from "react";

import { ErrorPage } from "@mobsuccess-devops/react-ui/ErrorPage";
import { useQueryClient } from "@tanstack/react-query";

import {
  recoverSession,
  type SessionRecovery,
} from "../../public/auth/session";
import { PendingPage } from "../PendingPage/PendingPage";

type SessionRecoveryFallbackProps = {
  retry: () => void;
};

// A route that crashed because the session expired under it (typically a tab
// left open overnight) is not a broken page. The session bootstrap decides:
// a dead session hands off to sign-in, a refreshed one retries the route, and
// only what the bootstrap cannot explain falls back to the error page.
export function SessionRecoveryFallback({
  retry,
}: SessionRecoveryFallbackProps): JSX.Element {
  const queryClient = useQueryClient();
  const [recovery, setRecovery] = useState<SessionRecovery | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const outcome = await recoverSession(queryClient);
      if (!active) {
        return;
      }
      if (outcome === "recovered") {
        retry();
        return;
      }
      setRecovery(outcome);
    })();
    return () => {
      active = false;
    };
  }, [queryClient, retry]);

  if (recovery === "unavailable") {
    return <ErrorPage.Any />;
  }
  return <PendingPage />;
}
